import React from 'react'
import { Tooltip } from 'antd'
import classNames from 'classnames'
import type { IconName } from '../../../shared/webcomponents/helpers/icons'

export type Size = 'large' | 'medium' | 'small' | 'tiny'

interface Props {
    id?: string
    iconName: IconName
    size: Size
    /** defaults to true */
    border?: boolean
    /** defaults to false */
    disabled?: boolean
    /** defaults to true */
    background?: boolean
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    className?: string
    tooltip?: string
    iconStyle?: React.CSSProperties
    'aria-label'?: string
    'aria-pressed'?: boolean
    'data-testid'?: string
    'data-insight'?: string
}

const sizeFactor: Record<Size, number> = {
    tiny: 1,
    small: 2,
    medium: 3,
    large: 4,
}

const tooltipStyle = {
    borderRadius: '0.4rem',
    padding: '0.4rem 1rem',
    minHeight: '0',
} as React.CSSProperties

/** Button for displaying an icon
 *
 * See:
 * * https://www.figma.com/file/BpMdzCsfZAXmpHQCowcsWD/Global-Design-Library-(Master-%40-7f17203)?type=design&node-id=0-1279&mode=design&t=5BwqlEWnLS3KMqux-0
 * * IconButton.stories.tsx
 */
export function IconButton({
    id,
    iconName,
    size,
    border,
    background,
    disabled,
    onClick,
    className: buttonClassName,
    tooltip,
    iconStyle,
    'aria-label': ariaLabel,
    'aria-pressed': ariaPressed,
    'data-testid': dataTestId,
    'data-insight': dataInsight,
}: Props) {
    border ??= true
    disabled ??= false
    background ??= true
    const button = (
        <button
            id={id}
            data-testid={dataTestId}
            data-insight={dataInsight}
            type="button"
            disabled={disabled}
            className={classNames(
                'AppCore__ui__IconButton',
                {
                    AppCore__ui__IconButtonNoBorder: !border,
                    AppCore__ui__IconButtonBackground: background,
                },
                buttonClassName
            )}
            style={{ '--size': sizeFactor[size] } as React.CSSProperties}
            onClick={onClick}
            aria-label={ariaLabel}
            aria-pressed={ariaPressed}
        >
            <zaptic-icon icon-name={iconName} style={iconStyle}></zaptic-icon>
        </button>
    )

    if (tooltip) {
        return (
            <Tooltip title={tooltip} color="var(--grey-800)" placement="top" overlayInnerStyle={tooltipStyle}>
                {button}
            </Tooltip>
        )
    }

    return button
}
